// src/components/common/Header.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QuanteraLogo from "../../assets/logo/quanteralogo.png";
import QuanteraLogoDark from "../../assets/logo/quanteralogodark.png";
import QuanteraLogoIcon from "../../assets/logo/quanteralogoicon.svg";
import VNFlag from "../../assets/flag-icon/vn.png";
import UKFlag from "../../assets/flag-icon/uk.jpg";
import { FaAngleDown } from "react-icons/fa6";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  setAccessLangCookie,
  isFunctionalCookieAllowed,
  subscribeCookieConsentChange,
} from "../../utils/langCookie";

function useClickOutsideMultiple(refs, onOutside) {
  useEffect(() => {
    function handle(evt) {
      const inside = refs.some(
        (r) => r.current && r.current.contains(evt.target)
      );
      if (!inside) onOutside();
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [refs, onOutside]);
}

const goTo = (id, after = () => { }) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  after();
};

const NavButton = ({ children, onClick, darkMode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      relative font-normal mx-5 tabletair:mx-1 tabletmini:mx-2 py-1 transition-colors
      text-[16px] tabletpro:text-[12px] tabletair:text-[10px] tabletmini:text-[8px]
      ${darkMode
        ? "text-neutral-13 hover:text-neutral-13/80"
        : "text-neutral-1 hover:text-white"
      }
      after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full
      after:opacity-0 after:transition-opacity after:duration-500 after:ease-in-out
      after:bg-gradient-to-r after:from-[#036AE5] after:to-[#58F8FF] hover:after:opacity-100 hover:after:shadow-[0_-4px_8px_#2A85E9]
    `}
  >
    {children}
  </button>
);

export default function QuanteraHeader({ lang }) {
  const { t, i18n } = useTranslation();

  const [openLang, setOpenLang] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const currentLang = i18n.language;
  const isVI = currentLang.startsWith("vi");
  const [darkMode, setDarkMode] = useState(false);

  const langRefMobile = useRef(null);
  const langRefDesktop = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  useClickOutsideMultiple([langRefMobile, langRefDesktop], () =>
    setOpenLang(false)
  );

  // Khóa scroll body khi mở menu
  useEffect(() => {
    if (openMenu) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [openMenu]);

  // ESC để đóng menu
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenu(false);
    };
    if (openMenu) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openMenu]);

  useEffect(() => {
    const handleConsentChange = () => {
      // Tắt functional → không dùng localStorage cho ngôn ngữ
      if (!isFunctionalCookieAllowed()) {
        try {
          localStorage.removeItem("lang");
        } catch {
          // ignore
        }
      }
    };

    return subscribeCookieConsentChange(handleConsentChange);
  }, []);

  useEffect(() => {
    const path = window.location.pathname;

    if (path.endsWith("/") && path !== "/") {
      const fixed = path.replace(/\/+$/, "");
      navigate(fixed, { replace: true });
    }
  }, []);


  const normalizePath = (p) => p.replace(/\/+$/, "");

  const switchLang = (lng) => {
    i18n.changeLanguage(lng);
    // setAccessLangCookie tự đọc lại consent (kể cả tab khác vừa tắt)
    setAccessLangCookie(lng);

    const basePath = normalizePath(
      location.pathname.replace(/^\/(vi|en)/, "")
    );

    const newPath = `/${lng}${basePath ? `/${basePath}` : ""}`;

    navigate(newPath, { replace: true });
  };


  useEffect(() => {
    document.title = t("common.pageTitle");
  }, [i18n.language, t]);

  // Theo dõi section để bật/tắt darkMode cho header
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const DARK_IDS = new Set(["about", "solutions2", "partners"]);
    const SECTION_IDS = [
      "about",
      "solutions",
      "solutions2",
      "apps",
      "ecosystem",
      "partners",
      "contact",
    ];

    const lastScrollYRef = { current: window.scrollY };
    // idx = -1 là vùng HERO (trước section đầu tiên)
    const curIdxRef = { current: -1 };

    const readSections = () => {
      return SECTION_IDS.map((id) => {
        const el = document.getElementById(id);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const height = el.offsetHeight || rect.height || 0;
        const bottom = top + height;
        return { id, top, bottom, height };
      }).filter(Boolean);
    };

    let sections = readSections();

    const anchorY = () => window.scrollY;

    const locateIndex = (y) => {
      if (!sections.length) return -1;
      if (y < sections[0].top) return -1; // HERO
      let idx = sections.findIndex((s) => y >= s.top && y < s.bottom);
      if (idx === -1) idx = sections.length - 1;
      return idx;
    };

    // Init
    const initIdx = locateIndex(anchorY());
    curIdxRef.current = initIdx;
    setDarkMode(initIdx === -1 ? false : DARK_IDS.has(sections[initIdx].id));

    const onScroll = () => {
      if (!sections.length) return;

      const y = anchorY();
      const goingDown = window.scrollY >= lastScrollYRef.current;
      lastScrollYRef.current = window.scrollY;

      let idx = curIdxRef.current;

      if (goingDown) {
        if (idx === -1) {
          if (y >= sections[0].top) idx = 0;
        } else {
          while (idx < sections.length - 1 && y >= sections[idx].bottom)
            idx += 1;
        }
      } else {
        if (idx <= 0) {
          if (y < sections[0].top) idx = -1;
        } else {
          while (idx > 0 && y < sections[idx].top) idx -= 1;
        }
      }

      if (idx !== curIdxRef.current) {
        curIdxRef.current = idx;
        const wantDark = idx === -1 ? false : DARK_IDS.has(sections[idx].id);
        setDarkMode(wantDark);
      }
    };

    const onResize = () => {
      sections = readSections();
      const idx = locateIndex(anchorY());
      curIdxRef.current = idx;
      setDarkMode(idx === -1 ? false : DARK_IDS.has(sections[idx].id));
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const flagSrc = isVI ? VNFlag : UKFlag;
  const flagAlt = isVI ? "Vietnamese" : "English";
  const langLabel = isVI ? "VI" : "EN";
  const blogsUrl = isVI
    ? "https://quantera.vn/blogs/"
    : "https://quantera.vn/blogs/en/";

  const openBlogs = () => {
    window.location.href = blogsUrl;
  };

  const headerTextClass = darkMode ? "text-neutral-13" : "text-neutral-1";

  return (
    <header
      className={`
        fixed top-0 inset-x-0 z-50
        transition-transform duration-300 ease-out shadow-md
      `}
    >
      <div
        className={`px-4 tabletpro:px-10 tabletair:px-4 tabletmini:px-4 backdrop-blur-[30px] border-b border-white/10 ${darkMode ? "bg-white/20" : "bg-white/20"
          }`}
      >
        <div
          className={`flex items-center justify-between max-w-[1400px] h-[79px] mx-auto ${headerTextClass}`}
        >
          {/* Logo */}
          <div className="flex items-center">
            <Link to={`/${lang}`} title={t("common.pageTitle")} aria-label={t("common.pageTitle")}>
              <img
                src={darkMode ? QuanteraLogoDark : QuanteraLogo}
                alt={t("common.pageTitle")}
                className="hidden desktop:block w-[192px] h-[50px]"
              />
            </Link>

            {/* Icon cho mobile/tablet */}
            <Link to={`/${lang}`} title={t("common.pageTitle")} aria-label={t("common.pageTitle")}>
              <img
                src={QuanteraLogoIcon}
                alt="Quantera Icon"
                className="desktop:hidden w-[152px] h-[40px] -ml-[30%]"
              />
            </Link>
          </div>

          {/* Right controls (mobile/tablet) */}
          <div
            className={`flex items-center gap-2 desktop:hidden ${headerTextClass}`}
          >
            {/* Language dropdown mobile */}
            <div ref={langRefMobile} className="relative">
              <button
                type="button"
                onClick={() => setOpenLang((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-2 text-[14px]"
              >
                <img
                  src={flagSrc}
                  alt={flagAlt}
                  className="w-[20px] h-[20px] rounded-full object-cover"
                />
                <span className="text-sm font-normal">{langLabel}</span>
                <FaAngleDown
                  className={`w-6 h-3 ${darkMode
                    ? "text-neutral-13 hover:text-neutral-13/80"
                    : "text-neutral-1 hover:text-white"
                    }`}
                />
              </button>

              {openLang && (
                <div
                  className={`absolute right-0 mt-2 w-[140px] overflow-hidden rounded-xl border border-white/10 ${darkMode
                    ? "bg-neutral-10 backdrop-blur-[8px] shadow-lg"
                    : "bg-neutral-10 backdrop-blur-[8px] shadow-lg"
                    }`}
                >
                  <button
                    type="button"
                    onClick={() => switchLang("en")}
                    className={`w-full px-3 py-2 text-sm text-left ${darkMode
                      ? "text-neutral-1 hover:bg-brandBlue-500"
                      : "text-neutral-1 hover:bg-brandBlue-500"
                      }`}
                  >
                    English
                  </button>
                  <div className="h-px bg-white/20" />
                  <button
                    type="button"
                    onClick={() => switchLang("vi")}
                    className={`w-full px-3 py-2 text-sm text-left ${darkMode
                      ? "text-neutral-1 hover:bg-brandBlue-500"
                      : "text-neutral-1 hover:bg-brandBlue-500"
                      }`}
                  >
                    Tiếng Việt
                  </button>
                </div>
              )}
            </div>

            {/* Hamburger */}
            <button
              type="button"
              onClick={() => {
                setOpenMenu(true);
                // cuộn lên đầu trang khi mở menu
                if (typeof window !== "undefined") {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className="p-2"
              aria-label="Open menu"
            >
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>

          {/* Desktop nav */}
          <nav
            className={`hidden desktop:flex items-center text-[16px] ${headerTextClass}`}
          >
            <NavButton darkMode={darkMode} onClick={() => goTo("about")}>
              {t("header.nav.about")}
            </NavButton>
            <NavButton darkMode={darkMode} onClick={() => goTo("solutions")}>
              {t("header.nav.solutions")}
            </NavButton>
            <NavButton darkMode={darkMode} onClick={() => goTo("apps")}>
              {t("header.nav.apps")}
            </NavButton>
            <NavButton darkMode={darkMode} onClick={() => goTo("ecosystem")}>
              {t("header.nav.ecosystem")}
            </NavButton>
            <NavButton darkMode={darkMode} onClick={() => goTo("partners")}>
              {t("header.nav.partners")}
            </NavButton>
            <NavButton darkMode={darkMode} onClick={openBlogs}>
              {t("header.nav.blogs")}
            </NavButton>
            <NavButton darkMode={darkMode} onClick={() => goTo("contact")}>
              {t("header.nav.contact")}
            </NavButton>

            <button
              type="button"
              className={`
                hidden sm:inline-flex items-center rounded-full border px-4 py-2 transition-all mr-4
                text-[16px] tabletpro:text-[12px] tabletair:text-[10px] tabletmini:text-[8px]
                hover:shadow-[0_0_10px_2px_rgba(0,166,255,0.6)] font-normal bg-brandBlue-500
                ${darkMode
                  ? "bg-brandBlue-500 hover:bg-brandBlue-600 text-white border-brandBlue-700 hover:border-[#8BBAF3]"
                  : "bg-brandBlue-500 hover:bg-brandBlue-600 text-white border-brandBlue-700 hover:border-[#8BBAF3]"
                }
              `}
              onClick={() => goTo("contact")}
            >
              {t("header.nav.trynow")}
            </button>

            {/* Language desktop */}
            <div ref={langRefDesktop} className="relative">
              <button
                type="button"
                onClick={() => setOpenLang((v) => !v)}
                className="inline-flex items-center gap-2 py-2 text-[14px]"
                aria-haspopup="listbox"
                aria-expanded={openLang}
              >
                <img
                  src={flagSrc}
                  alt={flagAlt}
                  className="w-[20px] h-[20px] rounded-full object-cover"
                />
                <span className="text-sm font-normal">{langLabel}</span>
                <FaAngleDown
                  className={`w-6 h-3 ${darkMode
                    ? "text-neutral-13 hover:text-neutral-13/80"
                    : "text-neutral-1 hover:text-white"
                    }`}
                />
              </button>

              {openLang && (
                <div
                  className={`absolute right-0 mt-2 w-[140px] rounded-xl border border-white/10 ${darkMode
                    ? "bg-neutral-10 backdrop-blur-[8px] shadow-lg"
                    : "bg-neutral-10 backdrop-blur-[8px] shadow-lg"
                    }`}
                  role="listbox"
                >
                  <button
                    type="button"
                    onClick={() => switchLang("en")}
                    className={`w-full px-3 py-2 text-sm text-left ${darkMode
                      ? "text-neutral-1 hover:rounded-t-xl hover:bg-brandBlue-500"
                      : "text-neutral-1 hover:rounded-t-xl hover:bg-brandBlue-500"
                      }`}
                    role="option"
                    aria-selected={!isVI}
                  >
                    English
                  </button>
                  <div className="h-px bg-white/20" />
                  <button
                    type="button"
                    onClick={() => switchLang("vi")}
                    className={`w-full px-3 py-2 text-sm text-left ${darkMode
                      ? "text-neutral-1 hover:rounded-b-xl hover:bg-brandBlue-500"
                      : "text-neutral-1 hover:rounded-b-xl hover:bg-brandBlue-500"
                      }`}
                    role="option"
                    aria-selected={isVI}
                  >
                    Tiếng Việt
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile overlay menu (always mounted for smooth exit animation) */}
      <div
        id="mobileOverlay"
        className={`
          fixed inset-0 z-[999]
          ${openMenu ? "pointer-events-auto" : "pointer-events-none"}
        `}
        role="dialog"
        aria-modal="true"
        aria-hidden={!openMenu}
        onClick={(e) => {
          // click vào nền (không phải panel) thì đóng
          if (e.target === e.currentTarget) setOpenMenu(false);
        }}
      >
        {/* Backdrop blur + fade */}
        <div
          className={`
            absolute inset-0
            backdrop-blur-[50px]
            transition-opacity duration-500 ease-out motion-reduce:transition-none
            ${openMenu ? "opacity-100" : "opacity-0"}
          `}
        />

        {/* Panel trượt (full screen) */}
        <div
          className={`
            absolute inset-0
            flex flex-col items-center justify-center px-8 text-white
            transition-transform duration-500 ease-out motion-reduce:transition-none
            ${openMenu ? "translate-x-0" : "translate-x-full"}
          `}
        >
          <button
            onClick={() => setOpenMenu(false)}
            className="absolute left-4 top-4 p-2 text-neutral-1/90 hover:text-white"
            aria-label="Close menu"
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <a href="/" title={t("common.pageTitle")} aria-label={t("common.pageTitle")}>
            <img
              src={QuanteraLogo}
              alt={t("common.pageTitle")}
              className="desktop:block w-[192px] h-[50px] mb-6"
            />
          </a>

          <ul className="flex flex-col items-center gap-6">
            {["about", "solutions", "apps", "ecosystem", "partners"].map((id) => (
              <li key={id}>
                <button
                  className="text-2xl md:text-3xl font-medium hover:opacity-90"
                  onClick={() => goTo(id, () => setOpenMenu(false))}
                >
                  {t(`header.nav.${id}`)}
                </button>
              </li>
            ))}
            <li>
              <a
                href={blogsUrl}
                className="text-2xl md:text-3xl font-medium hover:opacity-90"
                onClick={() => setOpenMenu(false)}
              >
                {t("header.nav.blogs")}
              </a>
            </li>
            <li>
              <button
                className="text-2xl md:text-3xl font-medium hover:opacity-90"
                onClick={() => goTo("contact", () => setOpenMenu(false))}
              >
                {t("header.nav.contact")}
              </button>
            </li>
          </ul>

          <button
            onClick={() => goTo("contact", () => setOpenMenu(false))}
            className="mt-10 inline-flex items-center rounded-full bg-brandBlue-500 hover:bg-brandBlue-600 text-white text-lg px-6 py-3 border border-brandBlue-700"
          >
            {t("header.nav.trynow")}
          </button>
        </div>
      </div>
    </header>
  );
}
